import React from 'react'
import SEO from 'src/components/common/SEO'
import Layout from 'src/components/common/Layout'

import { LogoSyncContextProvider } from 'src/components/common/AnnotationContext'
import { createUrl } from 'src/modules/utils/createUrl'
import { useSelector } from 'react-redux'
import { selectAnalyticsParameters } from 'src/modules/reducers/analytics'
import { Link } from 'gatsby'
import RSNGeolocationBannerNew from 'src/components/common/RSNOnlyBannerNew'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import ZeeFamilyPlanCard from 'src/components/common/ZeeFamilyPlanCard'
import {
  ChannelSection,
  DealSection,
  ExtraCTABanner,
  FAQSection,
  NetworkSection,
  PlatformSection,
  HighlightSection,
  MatchTicker,
  RedzoneBanner,
  HeroSection,
  FanduelPlanCardsNew,
} from 'src/components/common'
import HeroFootNote from 'src/components/common/HeroSectionNew/components/HeroFootNote'
import { PlanCards } from 'src/components/common/PlanCards'
import { useIsTrial } from 'src/modules/hooks/useIsTrial'
import { useHeroImage } from 'src/modules/hooks/useHeroImage'
import HeaderSignIn from 'src/components/common/HeaderSignIn'
import { WEB_MARKETING_HEADER_SIGN_IN } from 'src/modules/reducers/cohorts/constants'
import { useCohortFlag } from 'src/modules/hooks/useCohortFlag'

const USTemplateDefault = ({
  lang = 'en',
  market = 'us',
  noRedirect,
  miceText,
  // Offer Code
  offerCode,
  offerBanner,
  // Addon Code
  addonCode,
  // Metadata
  pageTitle,
  pageDescription,
  // Hero Section
  logo,
  logoHeight,
  title,
  subtitle,
  ctaText,
  video,
  videoMobile,
  staticAnnotation,
  annotations,
  poster,
  posterMobile,
  noteLink,
  heroCtaNote,
  // Optional Highlight Section
  highlightImage,
  highlightHeadline,
  highlightSubtitle,
  highlightNote,
  highlightBackground,
  highlightShowHeadlineTop,
  highlightUrlParams,
  highlightCtaText,
  highlightSavingsText,
  highlightHeadlineTop,
  // Network Section
  networkHeadline,
  networkSubtitle,
  networkSet,
  customNetworks,
  // Others
  team,
  // AB Test for RSN
  showRSN,
  showRedzone,
  redzoneOfferCode,
  // Show offer input for unique codes
  hasOfferInput,
  // AB Test - Video vs Logo Soup
  noVideo = false,
  // Remove references to cable
  removeCable,
  // Charter Plan Cards
  hasCharterPlanCards,

  //Zee Family Card
  hasZeeFamilyPlanCard,
  // AB Test for MatchTicker
  matchtickerSportId,
  matchtickerLeagueName,
  matchtickerLeagueId,
  // Disable Free Trial
  disableFreeTrial,
  // CA Redirect URL
  caRedirectUrl,
  // Personalization IDs
  sportId,
  leagueId,
  teamId,
  // FanDuel Plan Cards
  hasFanduelPlanCards,
  fanduelRSNData,
  showFanduelWithPro,
  // Additional FAQs
  additionalFAQs,
  platformImage,
  platformHeadline,
  dealSavingsText,
  hideChannelSection = false,
  hideNetworkSection = false,
  faqListOverride,
  // Use specific FD offer codes for Reds
  redsOffer,
  // Plan Cards
  hasPlanCards,
  plans,
  plansSharedData,
  // Rate Plan Code to override channels sections
  ratePlanCode,
  disableAnchor = false,
  tagline,
  planCardsFootnote,
  noIndex,
  plansTitle,
  countdownTimer,
  isHeroDefault,
}) => {
  // Disable Free Trial Modifications
  const modifiedCtaText = ctaText
  const isTrial = useIsTrial(market)
  disableFreeTrial = disableFreeTrial ?? !isTrial
  const showHeaderSignIn = useCohortFlag(WEB_MARKETING_HEADER_SIGN_IN)
  const defaultPoster = useHeroImage(isHeroDefault)
  const posterImage = poster || (video ? null : defaultPoster)
  const modifiedNetworkSubtitle =
    networkSubtitle ||
    (disableFreeTrial && (lang === 'es' ? 'Cancela cuando quieras.' : 'Cancel online anytime.'))
  let metaDescription =
    pageDescription ||
    // TEMPORARY NBC DISPUTE
    // 'Stream live TV with ABC, CBS, FOX, NBC, ESPN & top channels without cable. DVR included. No contract, cancel anytime.'
    'Stream live TV with ABC, CBS, FOX, ESPN & top channels without cable. DVR included. No contract, cancel anytime.'

  if (team) {
    // TEMPORARY NBC DISPUTE
    // metaDescription = `Stream ${team} live online on ABC, CBS, FOX, NBC, and ESPN with Fubo. No contract is required - try it for free and cancel anytime.`
    metaDescription = `Stream ${team} live online on ABC, CBS, FOX, and ESPN with Fubo. No contract is required - try it for free and cancel anytime.`
  }

  const stateForProtobuf = useSelector(selectStateForProtobuf)

  const handleLocaleSwitch = () => {
    fireUIInteractionEvent(
      {
        element: noteUrl,
        component: 'lp_locale_switch_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  const originalParams = useSelector(selectAnalyticsParameters)
  let noteUrl
  if (noteLink) {
    noteUrl = createUrl(noteLink, originalParams)
  }

  let noteText =
    lang === 'es' ? (
      <Link to={noteUrl} onClick={handleLocaleSwitch}>
        To learn more in English, <span>click here</span>.
      </Link>
    ) : (
      <Link to={noteUrl} onClick={handleLocaleSwitch}>
        Para traducir a español, <span>haga clic aquí</span>.
      </Link>
    )

  const hasAnchor = (hasFanduelPlanCards || hasPlanCards) && !disableAnchor ? '#select-plan' : null
  const hasOffer = offerBanner || offerCode
  const heroFootnote = !disableFreeTrial ? <HeroFootNote lang={lang} showEspn={true} /> : null

  if (hasFanduelPlanCards || offerBanner === ' ') {
    offerBanner = null
  } else if (!hasOffer) {
    if (fanduelRSNData?.offerCode) {
      offerCode = fanduelRSNData?.offerCode
    }
  }

  const isShowNetworkSection = hideNetworkSection ? false : !hasCharterPlanCards

  return (
    <Layout
      lang={lang}
      market={market}
      offerCode={offerCode}
      offerBanner={offerBanner}
      addonCode={addonCode}
      ctaHrefOverride={hasAnchor}
      noRedirect={noRedirect}
      miceText={miceText}
      caRedirectUrl={caRedirectUrl}
      sportId={sportId}
      leagueId={leagueId}
      teamId={teamId}
      noIndex={noIndex}
    >
      {showHeaderSignIn && (
        <HeaderSignIn
          lang={lang}
          market={market}
          disableFreeTrial={disableFreeTrial}
          ctaText={modifiedCtaText}
        />
      )}

      <LogoSyncContextProvider annotations={annotations}>
        <SEO lang={lang} market={market} title={pageTitle || title} description={metaDescription} />
        {matchtickerLeagueId && (
          <>
            <MatchTicker
              lang={lang}
              market={market}
              sportId={matchtickerSportId}
              leagueName={matchtickerLeagueName}
              leagueId={matchtickerLeagueId}
              ctaText={modifiedCtaText}
              disableFreeTrial={disableFreeTrial}
            />
          </>
        )}

        <HeroSection
          logo={logo}
          logoHeight={logoHeight}
          lang={lang}
          market={market}
          heroBgVideo={video}
          heroBgVideoMobile={videoMobile}
          title={title}
          subtitle={subtitle}
          countdownTimer={countdownTimer}
          ctaText={modifiedCtaText}
          staticAnnotation={staticAnnotation}
          posterImg={posterImage}
          posterImgMobile={posterMobile}
          noteText={heroCtaNote}
          hasOfferInput={hasOfferInput}
          noVideo={noVideo}
          ctaHrefOverride={hasAnchor}
          offerCode={offerCode}
          addonCode={addonCode}
          footnote={heroFootnote}
          disableFreeTrial={disableFreeTrial}
          tagline={tagline}
        />

        {showRSN && (
          <RSNGeolocationBannerNew
            hasOfferInput={hasOfferInput}
            ctaText={modifiedCtaText}
            ctaHrefOverride={hasAnchor}
            disableFreeTrial={disableFreeTrial}
            lang={lang}
            market={market}
          />
        )}
        {showRedzone && (
          <RedzoneBanner
            redzoneOfferCode={redzoneOfferCode}
            ctaText={modifiedCtaText}
            disableFreeTrial={disableFreeTrial}
          />
        )}

        {highlightImage && (
          <HighlightSection
            image={highlightImage}
            title={highlightHeadline}
            subtitle={highlightSubtitle}
            miceText={highlightNote}
            background={highlightBackground}
            showHeadlineTop={highlightShowHeadlineTop}
            savingsText={highlightSavingsText}
            disableFreeTrial={disableFreeTrial}
            market={market}
            lang={lang}
            urlParams={highlightUrlParams}
            ctaText={highlightCtaText}
          />
        )}
        {hasFanduelPlanCards && (
          <FanduelPlanCardsNew rsnData={fanduelRSNData} showFanduelWithPro={showFanduelWithPro} />
        )}

        {hasZeeFamilyPlanCard && (
          <ZeeFamilyPlanCard lang={lang} market={market} ctaText={modifiedCtaText} />
        )}
        {hasPlanCards && (
          <PlanCards
            lang={lang}
            market={market}
            plans={plans}
            sharedData={plansSharedData}
            footnote={planCardsFootnote}
            title={plansTitle}
          />
        )}

        {isShowNetworkSection && (
          <NetworkSection
            lang={lang}
            market={market}
            headline={networkHeadline}
            subtitle={modifiedNetworkSubtitle}
            networkSet={networkSet}
            customNetworks={customNetworks}
            ctaText={modifiedCtaText}
            hasOfferInput={hasOfferInput}
            ctaHrefOverride={hasAnchor}
            disableFreeTrial={disableFreeTrial}
          />
        )}
        {!hideChannelSection && (
          <ChannelSection
            lang={lang}
            market={market}
            title={
              removeCable &&
              (lang === 'es'
                ? 'Disfruta de los deportes, programas, noticias y películas que te encantan.'
                : 'Enjoy the sports, shows, news and movies you love')
            }
            initialChannelCount={hasCharterPlanCards && 116}
            zeeFamily={hasZeeFamilyPlanCard}
            ratePlanCode={ratePlanCode}
          />
        )}
        <DealSection
          lang={lang}
          market={market}
          title={
            removeCable && (lang === 'es' ? '¿Por qué elegir Fubo?' : 'Why you should choose Fubo')
          }
          savingsText={dealSavingsText}
        />
        <PlatformSection
          lang={lang}
          market={market}
          ctaText={modifiedCtaText}
          hasOfferInput={hasOfferInput}
          ctaHrefOverride={hasAnchor}
          platformImage={platformImage}
          platformHeadline={platformHeadline}
          disableFreeTrial={disableFreeTrial}
        />
        {!disableFreeTrial && (
          <ExtraCTABanner
            hasOfferInput={hasOfferInput}
            ctaText={modifiedCtaText}
            ctaHrefOverride={hasAnchor}
            market={market}
            lang={lang}
            disableFreeTrial={disableFreeTrial}
          />
        )}
        <FAQSection
          lang={lang}
          market={market}
          team={team}
          removeCable={removeCable}
          additionalFAQs={additionalFAQs}
          faqListOverride={faqListOverride}
        />
      </LogoSyncContextProvider>
    </Layout>
  )
}

export default USTemplateDefault
